import type { ReactElement } from 'react';
import { Focusable } from 'react-aria-components';
import {
  ButtonIcon,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import {
  RiDeleteBinLine,
  RiDownloadLine,
  RiEditLine,
  RiFileTextLine,
  RiMore2Line,
  RiRefreshLine,
} from '@remixicon/react';
import type { TrackDefinition } from '@internal/backstage-plugin-pulse-common';
import type { TrackActionReasons } from './track-action-reasons';

const disabledItemStyle = { opacity: 0.5, cursor: 'not-allowed' as const };

const actionContent = (icon: ReactElement, text: string, reason?: string) => {
  const content = (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--bui-space-2)',
        flexGrow: 1,
        alignSelf: 'stretch',
      }}
    >
      {icon}
      {text}
    </span>
  );

  return reason ? (
    <TooltipTrigger delay={0}>
      <Focusable>{content}</Focusable>
      <Tooltip placement="left">{reason}</Tooltip>
    </TooltipTrigger>
  ) : (
    content
  );
};

export type TrackActionsMenuProps = {
  track: TrackDefinition;
  reasons: TrackActionReasons;
  onExport: () => void;
  onEdit: () => void;
  onDeleteRequest: () => void;
  onRecertifyRequest: () => void;
  onOpenDocumentation: () => void;
};

/** Track actions button and its dropdown menu (export/edit/delete/recertify/docs). */
export const TrackActionsMenu = ({
  track,
  reasons,
  onExport,
  onEdit,
  onDeleteRequest,
  onRecertifyRequest,
  onOpenDocumentation,
}: TrackActionsMenuProps) => {
  const { editReason, deleteReason, recertifyReason, documentationReason } =
    reasons;

  return (
    <MenuTrigger>
      <ButtonIcon
        icon={<RiMore2Line />}
        aria-label="Track actions"
        variant="secondary"
      />
      <Menu placement="bottom end" disabledKeys={reasons.disabledKeys}>
        <MenuSection
          title={track.source === 'db' ? 'UI configured' : 'YAML configured'}
        >
          <MenuItem
            id="export"
            iconStart={<RiDownloadLine />}
            onAction={onExport}
          >
            Export
          </MenuItem>
          <MenuItem
            id="edit"
            style={editReason ? disabledItemStyle : undefined}
            onAction={onEdit}
          >
            {actionContent(<RiEditLine size={16} />, 'Edit', editReason)}
          </MenuItem>
          <MenuItem
            id="delete"
            color="danger"
            style={deleteReason ? disabledItemStyle : undefined}
            onAction={onDeleteRequest}
          >
            {actionContent(
              <RiDeleteBinLine size={16} />,
              'Delete',
              deleteReason,
            )}
          </MenuItem>
          <MenuItem
            id="recertify"
            style={recertifyReason ? disabledItemStyle : undefined}
            onAction={onRecertifyRequest}
          >
            {actionContent(
              <RiRefreshLine size={16} />,
              'Recertify',
              recertifyReason,
            )}
          </MenuItem>
          <MenuItem
            id="documentation"
            style={documentationReason ? disabledItemStyle : undefined}
            onAction={onOpenDocumentation}
          >
            {actionContent(
              <RiFileTextLine size={16} />,
              'Documentation',
              documentationReason,
            )}
          </MenuItem>
        </MenuSection>
      </Menu>
    </MenuTrigger>
  );
};
